import { getUserAccount } from './user';
import API from '../../utils/api';
import * as types from './types';
import { defaultLocale } from '../../utils/translation';
import { activeRequests, addActiveRequest, deleteActiveRequest } from '../../utils/network';
import { AGENT_SERVICE_ENDPOINT_URL, TICKETS_PAGE_SIZE } from '../../constants/defaultValues';
import { showErrorNotification } from '../../utils/render';
import { printTicket } from './betSlip';
import moment from 'moment';
import { TICKET_STATUSES } from '../../constants/enums';
import { changeErrorModalProps, resetErrorModalProps } from './global';

// GET ALL TICKETS ACTION CREATOR
export const getTickets = (filters, pageNumber) => async dispatch => {
    const timestamp = new Date().getTime();
    const activeRequestKey = 'agent/tickets';
    deleteActiveRequest(activeRequestKey);
    addActiveRequest(activeRequestKey, timestamp);

    try {
        dispatch({
            type: types.GET_TICKETS_REQUEST,
        });

        const { source, status, startTime, endTime, order, voucherCode } = filters;

        const params = {
            origin: source,
            status,
            startTime,
            endTime,
            order,
            voucherCode,
            pageSize: TICKETS_PAGE_SIZE,
            pageIndex: pageNumber,
        };

        const response = await API.get(`${AGENT_SERVICE_ENDPOINT_URL}/tickets`, {
            params,
        });

        const { info, items } = response.data;
        if (info.success) {
            const activeRequestTimestamp = activeRequests[activeRequestKey];
            if (timestamp !== activeRequestTimestamp) return;

            dispatch({
                type: types.GET_TICKETS_SUCCESS,
                payload: items,
                hasMoreTickets: items.length >= 10,
            });
        }
    } catch (error) {
        console.error({ error });
    }
};

// PAY WIN ACTION CREATOR
export const payWin =
    (ticketCode, { ticketStatus }) =>
    async dispatch => {
        dispatch({
            type: types.PAY_WIN_REQUEST,
        });

        try {
            const res = await API.put(`${AGENT_SERVICE_ENDPOINT_URL}/tickets/payout/${ticketCode}`);

            const { info, item } = res.data;

            if (info.success) {
                dispatch(getUserAccount());
                dispatch({
                    type: types.PAY_WIN_SUCCESS,
                    payload: item,
                });
                const ticketInfo = await dispatch(getTicket(ticketCode));

                return {
                    ...item,
                    status:
                        ticketStatus === TICKET_STATUSES.refunded
                            ? ticketStatus
                            : ticketInfo.status,
                    paidAt: moment(ticketInfo.paidAt).format('DD/MM/YYYY HH:mm:ss'),
                    paidBy: ticketInfo.paidBy,
                    payoutAmount: ticketInfo.payoutAmount,
                };
            }
        } catch (err) {
            const { info } = err.response.data;
            dispatch({
                type: types.PAY_WIN_FAILED,
                payload: info,
                error: true,
            });
        }
    };

// CANCEL TICKET ACTION CREATOR
export const cancelTicket = ticketCode => async dispatch => {
    dispatch({
        type: types.CANCEL_TICKET_REQUEST,
    });

    try {
        const res = await API.put(`${AGENT_SERVICE_ENDPOINT_URL}/tickets/cancel/${ticketCode}`);
        const { info, item } = res.data;

        if (info.success) {
            dispatch(getUserAccount());
            dispatch({
                type: types.CANCEL_TICKET_SUCCESS,
                cancelSuccess: info.success,
                canceledTicket: ticketCode,
            });
            const ticketInfo = await dispatch(getTicket(ticketCode));

            return {
                ...item,
                status: ticketInfo.status,
                paidAt: moment(ticketInfo.paidAt).format('DD/MM/YYYY HH:mm:ss'),
                paidBy: ticketInfo.paidBy,
                payoutAmount: ticketInfo.payoutAmount,
            };
        }
    } catch (err) {
        const { info } = err.response.data;
        dispatch({
            type: types.CANCEL_TICKET_FAILED,
            payload: info,
            error: true,
        });
    }
};

export const clearTickets = () => dispatch => dispatch({ type: types.CLEAR_TICKETS });

export const clearTicket = () => dispatch => dispatch({ type: types.CLEAR_TICKET });

export const sendReprintRequest = ticketCode => async dispatch => {
    try {
        const data = await API.post(`${AGENT_SERVICE_ENDPOINT_URL}/reprint`, {
            ticketNumber: ticketCode,
        });

        const { info, item } = data.data;

        if (!info.success) {
            showErrorNotification(info.errors[0].reason || 'Something went wrong');
            return;
        }

        return item;
    } catch (error) {
        showErrorNotification(
            error.response?.data?.info?.errors[0].reason || 'Something went wrong'
        );
    }
};

export const reprintTicket = ticketCode => async (dispatch, getStore) => {
    try {
        const data = await API.get(`${AGENT_SERVICE_ENDPOINT_URL}/tickets/print/${ticketCode}`);

        const { info, item } = data.data;

        if (!info.success) {
            showErrorNotification(info.errors[0].reason || 'Something went wrong');
            return;
        }

        const store = getStore();
        const { ticket: ticketInfo } = store.tickets.ticketModal;

        const newTicketInfo = { ...ticketInfo, printLimitExceeded: true };

        dispatch(
            changeTicketModalProps({
                ticket: newTicketInfo,
            })
        );

        item.status = ticketInfo.status;
        item.paidAt = moment(ticketInfo.paidAt).format('DD/MM/YYYY HH:mm:ss');
        item.paidBy = ticketInfo.paidBy;
        item.payoutAmount = ticketInfo.payoutAmount;
        item.reprintDate = moment().format('DD/MM/YYYY HH:mm:ss');
        await printTicket(item);
    } catch (error) {
        showErrorNotification(
            error.response?.data?.info?.errors[0].reason || 'Something went wrong'
        );
    }
};

export const getTicket = ticketCode => async dispatch => {
    dispatch(resetErrorModalProps());
    // dispatch(resetTicketModalProps());
    dispatch(changeTicketModalProps({ loading: true }));

    try {
        const lang = localStorage.getItem('lang') || defaultLocale;

        const res = await API.get(`${AGENT_SERVICE_ENDPOINT_URL}/tickets/${ticketCode}`, {
            headers: { 'x-lang': lang },
        });

        const { info, item } = res.data;

        if (info.success) {
            dispatch(
                changeTicketModalProps({
                    loading: false,
                    ticket: item,
                })
            );
            // dispatch(resetErrorModalProps());

            return item;
        }
    } catch (err) {
        const { info } = err.response.data;
        dispatch(resetTicketModalProps());
        dispatch(changeErrorModalProps({ isVisible: true, errors: info.errors }));
    }
};

export const changeTicketModalProps = payload => ({
    type: types.TICKET_MODAL_PROPS_CHANGED,
    payload,
});

export const resetTicketModalProps = payload => ({
    type: types.TICKET_MODAL_PROPS_RESET,
    payload,
});
